import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useKvkkConsentLedger, type KvkkLedgerRow } from "@/hooks/use-kvkk-consent-ledger";
import { downloadFromSignedUrl, generateKvkkPdfReport } from "@/lib/kvkk-consent-report";
import { Loader2, FileDown, RefreshCw, MessageSquare, ShieldCheck, ShieldX, ShieldAlert, Shield, ShieldOff } from "lucide-react";

type Props = {
  phone: string | null;
  customerId: string | null;
  onSendSms?: () => void;
  sendingSms?: boolean;
  consentDeclinedMessage?: string;
  onRefreshStatus?: () => void;
  statusLoading?: boolean;
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("tr-TR");
};

type StatusUi = {
  label: string;
  badgeCls: string;
  icon: React.ReactNode;
  rowCls: string;
};

const statusUi = (status: string | null | undefined): StatusUi => {
  if (status === "granted")
    return {
      label: "Onay Verildi",
      badgeCls: "bg-green-500/15 text-green-700 border-green-200",
      icon: <ShieldCheck className="w-4 h-4 text-green-600" />,
      rowCls: "bg-green-500/5 border-green-200/60",
    };
  if (status === "withdrawn")
    return {
      label: "Geri Çekildi",
      badgeCls: "bg-red-500/15 text-red-700 border-red-200",
      icon: <ShieldX className="w-4 h-4 text-red-600" />,
      rowCls: "bg-red-500/5 border-red-200/60",
    };
  if (status === "declined" || status === "denied")
    return {
      label: "Reddedildi",
      badgeCls: "bg-red-500/15 text-red-700 border-red-200",
      icon: <ShieldX className="w-4 h-4 text-red-600" />,
      rowCls: "bg-red-500/5 border-red-200/60",
    };
  return {
    label: "Kayıt Yok",
    badgeCls: "bg-muted text-muted-foreground border-border",
    icon: <ShieldAlert className="w-4 h-4 text-muted-foreground" />,
    rowCls: "bg-muted/30 border-border",
  };
};

const isMeaningful = (value: unknown) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim();
  return str.length > 0 && str !== "-";
};

const getFromEvidence = (
  row: KvkkLedgerRow | null | undefined,
  keys: string[],
): string | null => {
  const evidence = row?.evidence_snapshot;
  if (!evidence || typeof evidence !== "object") return null;
  for (const key of keys) {
    const value = (evidence as Record<string, unknown>)[key];
    if (isMeaningful(value)) return String(value);
    if (key.includes(".")) {
      const nested = key
        .split(".")
        .reduce<unknown>((acc, part) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[part] : null), evidence);
      if (isMeaningful(nested)) return String(nested);
    }
  }
  return null;
};

type ResolvedKvkkField = {
  label: string;
  value: string;
  source: string;
  reason: string | null;
};

const toEvidenceSource = (key: string) => `evidence_snapshot.${key}`;

const resolveMissingReason = (fieldKey: string, rows: KvkkLedgerRow[]) => {
  const latestRow = rows[0];
  if (!latestRow) return "Bu telefon için KVKK kaydı bulunamadı.";

  const hasRequestId =
    isMeaningful(latestRow.request_id) ||
    isMeaningful(getFromEvidence(latestRow, ["request_id", "request.id"]));

  if (fieldKey === "sms_message_sent") {
    if (hasRequestId) return "SMS metni kayıt oluşturulurken evidence içine yazılmadığı için NULL.";
    return "SMS metni kaynağı bulunamadı.";
  }
  if (fieldKey === "sms_sent_at") {
    if (hasRequestId) return "SMS gönderim zamanı kayıt oluşturulurken evidence içine yazılmadığı için NULL.";
    return "SMS gönderim zamanı kaynağı bulunamadı.";
  }
  if (fieldKey === "policy_url") return "Politika URL alanı kayıt anında doldurulmamış.";
  if (fieldKey === "sms_reply_received") return "SMS yanıt metni kaydedilmemiş.";
  return "Bu alan ilgili KVKK kaydında saklanmamış.";
};

const resolveField = (
  rows: KvkkLedgerRow[],
  label: string,
  rowKeys: Array<keyof KvkkLedgerRow>,
  evidenceKeys: string[],
  fieldKeyForReason: string,
  formatAsDate = false,
): ResolvedKvkkField => {
  for (const row of rows) {
    for (const key of rowKeys) {
      const value = row[key];
      if (isMeaningful(value)) {
        return { label, value: formatAsDate ? formatDate(String(value)) : String(value), source: `kvkk_consent_ledger.${String(key)}`, reason: null };
      }
    }
    for (const key of evidenceKeys) {
      const fromEvidence = getFromEvidence(row, [key]);
      if (isMeaningful(fromEvidence)) {
        return { label, value: formatAsDate ? formatDate(String(fromEvidence)) : String(fromEvidence), source: toEvidenceSource(key), reason: null };
      }
    }
  }
  return { label, value: "Kayıt yok", source: "NULL", reason: resolveMissingReason(fieldKeyForReason, rows) };
};

const getCurrentStatusTimestamp = (latest: KvkkLedgerRow | null) => {
  if (!latest) return null;
  if (latest.consent_status === "withdrawn") {
    const fromEvidence = getFromEvidence(latest, ["decision_timestamp", "sms_replied_at", "event_timestamp"]);
    if (isMeaningful(fromEvidence)) return formatDate(fromEvidence);
  }
  return formatDate(latest.created_at);
};

export function KvkkConsentSection({ phone, customerId, onSendSms, sendingSms, consentDeclinedMessage, onRefreshStatus, statusLoading }: Props) {
  const [refreshToken, setRefreshToken] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [lastSignedUrl, setLastSignedUrl] = useState<string | null>(null);
  const { toast } = useToast();

  const query = useKvkkConsentLedger(phone, refreshToken);
  const latest = query.data?.latest ?? null;
  const history = query.data?.history ?? [];
  const hasAnyRecord = history.length > 0;
  const status = statusUi(latest?.consent_status);
  const timestamp = getCurrentStatusTimestamp(latest);

  const latestEvidencePretty = useMemo(() => {
    if (!latest?.evidence_snapshot) return "{}";
    return JSON.stringify(latest.evidence_snapshot, null, 2);
  }, [latest?.evidence_snapshot]);

  const canGenerate = !!phone && history.length > 0 && !generating;

  const latestResolvedFields = useMemo(() => {
    const rows = history.filter(Boolean);
    if (!rows.length) return [];
    return [
      resolveField(rows, "Onay Yöntemi", ["consent_method"], ["consent_method", "method"], "consent_method"),
      resolveField(rows, "Onay Kaynağı", ["consent_source"], ["consent_source", "source"], "consent_source"),
      resolveField(rows, "Geri Çekme Yöntemi", ["withdraw_method"], ["withdraw_method"], "withdraw_method"),
      resolveField(rows, "Politika Sürümü ID", ["policy_version_id"], ["policy_version_id"], "policy_version_id"),
      resolveField(rows, "Politika URL", ["policy_url"], ["policy_url", "policy.link"], "policy_url"),
      resolveField(rows, "SMS Gönderim Zamanı", ["sms_sent_at"], ["sms_sent_at", "sms.sent_at", "smsSentAt"], "sms_sent_at", true),
      resolveField(rows, "SMS Yanıt Zamanı", ["sms_replied_at"], ["sms_replied_at", "decision_timestamp", "sms.replied_at", "smsRepliedAt"], "sms_replied_at", true),
      resolveField(rows, "Gönderilen SMS Metni", ["sms_message_sent"], ["sms_message_sent", "sms_message", "sent_sms_text", "sms.message", "smsText"], "sms_message_sent"),
      resolveField(rows, "Alınan SMS Yanıtı", ["sms_reply_received"], ["sms_reply_received", "sms_reply", "reply_text", "sms.reply", "smsReply"], "sms_reply_received"),
    ];
  }, [history]);

  const handleGeneratePdf = async () => {
    if (!phone) return;
    try {
      setGenerating(true);
      const result = await generateKvkkPdfReport({ phone, customerId });
      setLastSignedUrl(result.signed_url);
      downloadFromSignedUrl(result.signed_url);
      toast({ title: "KVKK PDF Hazır", description: "Rapor üretildi, indirme otomatik başlatıldı." });
    } catch (error) {
      toast({ title: "Hata", description: error instanceof Error ? error.message : "KVKK PDF üretilemedi", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      {/* ── Header ── */}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base">KVKK Onay Durumu</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRefreshToken((v) => v + 1)}
              disabled={query.isLoading}
            >
              {query.isLoading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              )}
              Yenile
            </Button>
            <Button size="sm" onClick={handleGeneratePdf} disabled={!canGenerate}>
              {generating ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <FileDown className="w-3.5 h-3.5 mr-1.5" />
              )}
              {generating ? "Üretiliyor..." : "KVKK PDF Oluştur"}
            </Button>
          </div>
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="pt-4 pb-3 space-y-3">
        {/* ── Status Row (always visible) ── */}
        <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 flex-wrap ${status.rowCls}`}>
          {status.icon}
          <span className="text-sm text-muted-foreground">Güncel durum:</span>
          <Badge variant="outline" className={`text-xs font-medium ${status.badgeCls}`}>
            {query.isLoading ? "Yükleniyor..." : latest ? status.label : "Kayıt Yok"}
          </Badge>
          {timestamp && !query.isLoading && (
            <span className="text-xs text-muted-foreground">{timestamp}</span>
          )}
          {onSendSms && !consentDeclinedMessage && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSendSms}
              disabled={sendingSms || query.isLoading}
              className="ml-auto shrink-0"
            >
              {sendingSms ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
              )}
              {sendingSms ? "Gönderiliyor..." : "KVKK SMS Gönder"}
            </Button>
          )}
        </div>

        {/* ── Declined / Withdrawn Banner ── */}
        {consentDeclinedMessage && (
          <div className="rounded-lg border border-red-200 bg-red-50/60 px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              <ShieldOff className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700 leading-snug">{consentDeclinedMessage}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onSendSms && (
                <Button
                  size="sm"
                  onClick={onSendSms}
                  disabled={sendingSms || statusLoading}
                  className="shrink-0"
                >
                  {sendingSms ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {sendingSms ? "Gönderiliyor..." : "Tekrar KVKK SMS Gönder"}
                </Button>
              )}
              {onRefreshStatus && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRefreshStatus}
                  disabled={statusLoading}
                  className="shrink-0"
                >
                  {statusLoading ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {statusLoading ? "Kontrol ediliyor..." : "Durumu Yenile"}
                </Button>
              )}
            </div>
          </div>
        )}

        {query.isError && (
          <p className="text-sm text-destructive px-1">KVKK ledger okunamadı.</p>
        )}

        {!query.isLoading && !hasAnyRecord && !query.isError && (
          <p className="text-sm text-muted-foreground px-1">Bu müşteriye KVKK onayı daha önce sorulmamış.</p>
        )}

        {/* ── PDF re-download row ── */}
        {lastSignedUrl && (
          <div className="rounded-lg border px-4 py-2.5 flex items-center justify-between gap-2 bg-muted/30">
            <p className="text-sm text-muted-foreground">PDF oluşturuldu.</p>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => downloadFromSignedUrl(lastSignedUrl)}>
              <FileDown className="w-3 h-3 mr-1.5" />
              Tekrar İndir
            </Button>
          </div>
        )}

        {/* ── Accordion sandwich sections ── */}
        {hasAnyRecord && (
          <Accordion type="multiple" className="space-y-1">
            {/* Onay Detayları */}
            <AccordionItem value="details" className="border rounded-lg overflow-hidden">
              <AccordionTrigger className="px-4 py-2.5 text-sm font-medium hover:no-underline hover:bg-muted/40 data-[state=open]:bg-muted/40">
                Onay Detayları
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-0">
                <div className="divide-y">
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-muted/30 text-xs font-medium text-muted-foreground">
                    <div className="col-span-4">Alan</div>
                    <div className="col-span-5">Değer</div>
                    <div className="col-span-3">Kaynak</div>
                  </div>
                  {latestResolvedFields.map((field) => (
                    <div key={field.label} className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm">
                      <div className="col-span-4 font-medium text-foreground">{field.label}</div>
                      <div className="col-span-5 text-foreground">
                        {field.value}
                        {field.reason && (
                          <p className="text-xs text-muted-foreground mt-0.5">{field.reason}</p>
                        )}
                      </div>
                      <div className="col-span-3 text-xs text-muted-foreground break-all">{field.source}</div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Geçmiş */}
            <AccordionItem value="history" className="border rounded-lg overflow-hidden">
              <AccordionTrigger className="px-4 py-2.5 text-sm font-medium hover:no-underline hover:bg-muted/40 data-[state=open]:bg-muted/40">
                Geçmiş
                <span className="ml-2 text-xs font-normal text-muted-foreground">({history.length} kayıt)</span>
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Tarih</TableHead>
                        <TableHead className="text-xs">Olay</TableHead>
                        <TableHead className="text-xs">Durum</TableHead>
                        <TableHead className="text-xs">Yöntem</TableHead>
                        <TableHead className="text-xs">Kaynak</TableHead>
                        <TableHead className="text-xs">IP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((row) => (
                        <TableRow key={row.id} className="text-xs">
                          <TableCell className="whitespace-nowrap">{formatDate(row.created_at)}</TableCell>
                          <TableCell>{row.event_type}</TableCell>
                          <TableCell>{row.consent_status}</TableCell>
                          <TableCell>{row.consent_method || "-"}</TableCell>
                          <TableCell>{row.consent_source || "-"}</TableCell>
                          <TableCell>{row.ip_address || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Per-row detail accordions */}
                <Accordion type="single" collapsible className="px-3 pb-3 pt-1">
                  {history.map((row) => (
                    <AccordionItem key={row.id} value={`row-${row.id}`} className="border-0 border-b last:border-b-0">
                      <AccordionTrigger className="py-2 text-xs hover:no-underline text-muted-foreground">
                        {formatDate(row.created_at)} — {row.event_type} — {row.consent_status}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-1.5 text-xs text-foreground pl-2">
                          {row.sms_message_sent && (
                            <p><span className="font-medium text-muted-foreground">Gönderilen SMS:</span> {row.sms_message_sent}</p>
                          )}
                          {row.sms_reply_received && (
                            <p><span className="font-medium text-muted-foreground">Alınan Yanıt:</span> {row.sms_reply_received}</p>
                          )}
                          {row.user_agent && (
                            <p><span className="font-medium text-muted-foreground">Tarayıcı:</span> {row.user_agent}</p>
                          )}
                          <pre className="whitespace-pre-wrap break-all rounded-md bg-muted p-2.5 mt-1.5 text-xs">
                            {JSON.stringify(row.evidence_snapshot || {}, null, 2)}
                          </pre>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AccordionContent>
            </AccordionItem>

            {/* Ham Veri */}
            <AccordionItem value="raw" className="border rounded-lg overflow-hidden">
              <AccordionTrigger className="px-4 py-2.5 text-sm font-medium hover:no-underline hover:bg-muted/40 data-[state=open]:bg-muted/40">
                Kanıt Özeti (Ham Veri)
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 pt-2">
                <pre className="text-xs whitespace-pre-wrap break-all rounded-md bg-muted p-3">
                  {latestEvidencePretty}
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
